import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useApi from 'hooks/useApi';
import { setCurrentNotification } from 'redux/actions/notificationActions';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';

import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';

import { LabelIcon } from 'shared/components/Labels';
import { colors } from 'shared/defaultTheme';
import NotificationForm from 'shared/components/NotificationForm';

import { NotificationBackground } from './styles';

const PATH_ID = '1/';

const Notification = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { t } = useTranslation();
  const theme = useSelector((state) => state.theme);
  const { cusNavId, isCustomer, clientId, mail } = useSelector((state) => state.auth);
  const notificationState = useSelector((state) => state.notification);
  const [selectedClient, setSelectedClient] = useState('');
  const { pathname } = useLocation();
  const { mainColor } = theme;
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [clients, setClients] = useState([]);
  const [contacts, setContacs] = useState([]);
  const [currentClientId, setNewClient] = useState(null);
  const [disableButton, setDisabledButton] = useState(false);
  const newClient = useRef(null);

  useEffect(() => {
    if (notificationState?.currentNotification) {
      dispatch(
        addBreadcrumb({
          label: 'notification_edit',
          active: true,
          link: pathname,
          isModify: true,
        })
      );
    } else {
      dispatch(
        addBreadcrumb({
          label: 'notification_title_add',
          active: true,
          link: pathname,
          isModify: true,
        })
      );
    }
  }, []);

  const [{ loading: loadingCreate }, createNotification] = customAreaApi(
    {
      url: `${window.API_V1}/cli/notifications/${cusNavId}/${
        isCustomer ? newClient.current : clientId
      }/${encodeURIComponent(mail)}`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingUpdate }, updateNotification] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, getClientsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getContactsData] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const clientsData = async () => {
    const {
      data: { data },
    } = await getClientsData({
      data: {
        filters: [
          {
            model: 'Client',
            field: 'ClientId',
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    });
    if (notificationState?.currentNotification) {
      setSelectedClient(
        data.find((client) => client.id === notificationState?.currentNotification.clientId)
          .clientName
      );
    }
    setClients(data);
  };

  useEffect(() => {
    clientsData();
    return () => {
      dispatch(setCurrentNotification(null));
    };
  }, []);

  useEffect(() => {
    if (currentClientId) {
      const getContactList = async () => {
        const {
          data: { data },
        } = await getContactsData({
          url: `${window.API_V1}/cli/contacts/${cusNavId}/${
            isCustomer ? newClient.current : clientId
          }`,
        });

        const filterData = data.filter((x) => x.mail);
        setContacs(filterData);
      };
      getContactList();
    }
  }, [currentClientId]);

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onCreateNotification = async (values) => {
    try {
      setDisabledButton(true);
      let newDocument = [];
      if (notificationState.selectedFiles.length > 0) {
        notificationState.selectedFiles.forEach((file) => {
          newDocument = [
            ...newDocument,
            {
              id: file.id ? file.id : null,
              name: file.name,
              pathId: PATH_ID,
              size: file.size,
              data: file.data,
              permissions: [
                {
                  u: mail,
                  p: 'rw-',
                },
              ],
            },
          ];
        });
      }

      const newData = [
        {
          createdBy: mail,
          responsibles: values.responsibles,
          name: values.name,
          date: moment(values.date).toISOString(),
          linkToContact: values.linkToContact,
          description: values.description,
          answers: [],
          docs: newDocument,
        },
      ];
      const {
        data: { data },
      } = await createNotification({ data: newData });
      dispatch(removeBreadcrumb());
      dispatch(setCurrentNotification(null));
      history.push('/notifications');
      if (!data) {
        throw new Error(
          data.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setShowError(true);
      if (error.response?.data.errors && error.response?.data.errors[0].detail === 'CCNO-16') {
        setErrorMessage(t('general_error_notification'));
      } else {
        setErrorMessage(t('custom_field_operation_error_message'));
      }
      setDisabledButton(false);
    }
  };

  const onEditNotification = async (values) => {
    try {
      setDisabledButton(true);
      const { clientNavisionId, createdBy } = notificationState?.currentNotification;
      const newData = {
        id: notificationState?.currentNotification.id,
        createdBy,
        name: values.name,
        date: moment(values.date).toISOString(),
        linkToContact: values.linkToContact,
        responsibles: values.responsibles,
        description: values.description,
        answers: notificationState?.currentNotification.answers,
        docs: [],
      };

      const {
        data: { data },
      } = await updateNotification({
        data: newData,
        url: `${window.API_V1}/cli/notifications/${clientNavisionId}/${notificationState?.currentNotification.clientId}/modify`,
      });
      if (notificationState.selectedFiles.length > 0) {
        dispatch(removeBreadcrumb());
        dispatch(setCurrentNotification(null));
        history.push('/notifications');
      } else {
        dispatch(removeBreadcrumb());
        dispatch(setCurrentNotification(null));
        history.push('/notifications');
      }
      if (!data) {
        throw new Error(
          data.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setShowError(true);
      if (error.response?.data?.errors && error.response?.data.errors[0].detail === 'CCNO-16') {
        setErrorMessage(t('general_error_notification'));
      } else {
        setErrorMessage(t('custom_field_operation_error_message'));
      }
      setDisabledButton(false);
    }
  };

  const onSubmit = async (values) => {
    if (notificationState?.currentNotification) {
      onEditNotification(values);
    } else {
      onCreateNotification(values);
    }
  };

  const onCancel = () => {
    dispatch(removeBreadcrumb());
    dispatch(setCurrentNotification(null));
    history.push('/notifications');
  };

  return (
    <div className="pb-3">
      <NotificationBackground className="mr-2">
        <LabelIcon
          label={t('notification_communication_new')}
          Icon={() => <ConversationIcon fill={colors.white} className="label-icon__notification" />}
          color={mainColor}
          className="ml-1 label-icon text-uppercase"
          classNameMain="mb-4"
        />
        <NotificationForm
          onCancel={onCancel}
          onSubmit={onSubmit}
          onToggleError={onToggleError}
          showError={showError}
          errorMessage={errorMessage}
          clients={clients}
          contacts={contacts}
          newClient={newClient}
          setNewClient={setNewClient}
          loadingCreate={loadingCreate}
          loadingUpdate={loadingUpdate}
          disableButton={disableButton}
          selectedClient={selectedClient}
        />
      </NotificationBackground>
    </div>
  );
};

export default Notification;
